import { NavLink, useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { ROUTES } from '@/constants'

export interface SidebarItem {
  path: string
  label: string
  icon?: React.ReactNode
  activeClassName?: string
  /** If true, treat as logout: onClick navigates to login instead of path */
  isLogout?: boolean
}

interface SidebarBrand {
  logo: React.ReactNode
  subtitle?: string
}

interface SidebarProps {
  items: SidebarItem[]
  brand?: SidebarBrand
  onLogout?: () => void | Promise<void>
  /** When set, sidebar is a mobile overlay; open/onClose control visibility */
  open?: boolean
  onClose?: () => void
}

const defaultActiveClass = 'bg-primary-100 text-primary-700'

export const Sidebar: React.FC<SidebarProps> = ({
  items,
  brand,
  onLogout,
  open = false,
  onClose,
}) => {
  const navigate = useNavigate()

  const handleLogout = async () => {
    await onLogout?.()
    navigate(ROUTES.LOGIN)
  }

  const navContent = (
    <>
      {brand && (
        <div className="p-4 pl-8 border-b border-gray-100">
          <div className="flex flex-col gap-1">
            {brand.logo}
            {brand.subtitle && (
              <p className="text-sm font-medium text-gray-600">
                {brand.subtitle}
              </p>
            )}
          </div>
        </div>
      )}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-1">
          {items.map(item => {
            if (item.isLogout) {
              return (
                <li key="logout">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    {item.icon}
                    {item.label}
                  </button>
                </li>
              )
            }
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors',
                      isActive
                        ? (item.activeClassName ?? defaultActiveClass)
                        : 'text-gray-600 hover:bg-gray-100'
                    )
                  }
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              </li>
            )
          })}
        </ul>
      </nav>
    </>
  )

  /* Mobile: fixed overlay that slides in from the left */
  if (onClose !== undefined) {
    return (
      <>
        {/* Backdrop */}
        <div
          role="button"
          tabIndex={-1}
          aria-label="Close menu"
          onClick={onClose}
          onKeyDown={e => e.key === 'Escape' && onClose()}
          className={cn(
            'fixed inset-0 z-40 bg-black/50 transition-opacity md:hidden',
            open ? 'opacity-100' : 'pointer-events-none opacity-0'
          )}
        />
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 w-64 max-w-[85vw] bg-white shadow-xl flex flex-col transition-transform duration-200 ease-out shrink-0',
            'md:relative md:inset-auto md:z-auto md:translate-x-0 md:shadow-lg md:max-w-none',
            open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
          )}
          aria-hidden={!open}
        >
          <div className="flex items-center justify-between border-b border-gray-100 p-4 md:hidden shrink-0">
            <span className="text-sm font-medium text-gray-700">Menu</span>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {navContent}
        </aside>
      </>
    )
  }

  /* Desktop: static sidebar */
  return (
    <aside className="hidden w-64 shrink-0 bg-white shadow-lg flex-col md:flex">
      {navContent}
    </aside>
  )
}
